<script lang="ts" setup>
	import DetailTable from '@/components/table/detailtable/DetailTable.vue';
	import ProductTable from '@/components/table/producttable/ProductTable.vue';
	import {vScroll} from '@vueuse/components';
	import {useElementHover, type UseScrollReturn, useWindowSize} from '@vueuse/core';
	import {computed, ref} from 'vue';
	import {useFilters} from '@/store/filter.store';
	import type {StaticTableHeader, TableSort} from '@/types/table';
	import FixedDetailsTableHeader from '@/components/table/fixedtableheader/FixedDetailsTableHeader.vue';
	import {
		type Product,
		scrollToNextColumn,
		scrollToPrevColumn,
		syncFixedTableHeaderScrollPosition
	} from 'dummen-shared';

	defineProps<{
		productHeaders: Array<StaticTableHeader>;
		detailHeaders: Record<string, string>;
		products: Array<Product>;
	}>();

	const emit = defineEmits(['on-update-sort']);

	const scrollElement = ref();
	const isScrollStart = ref(true);
	const isScrollEnd = ref(false);

	const filterStore = useFilters();
	const {width} = useWindowSize();
	const scrollElementIsUsed = useElementHover(scrollElement);

	const mobileViewActive = computed(() => width.value < 640);

	const tableSort = computed(() => filterStore.sort);

	const onScroll = (state: UseScrollReturn) => {
		isScrollStart.value = state.arrivedState.left;
		isScrollEnd.value = state.arrivedState.right;

		// Only sync scrolling when this specific scroll element is used, to break the circle.
		// Without this it's not possible to use smooth scrolling.
		if (scrollElementIsUsed.value) {
			syncFixedTableHeaderScrollPosition();
		}
	};

	const updateSort = async (sort: TableSort) => {
		filterStore.setSort(sort);
		emit('on-update-sort');
	};

</script>

<template>
	<FixedDetailsTableHeader v-if="!mobileViewActive"
	                         :detail-headers="detailHeaders"
	                         :product-headers="productHeaders"
	                         :table-sort="tableSort"
	                         @on-update-sort="updateSort" />

	<div class="catalog-overview-table-wrapper js-catalog-overview-table-wrapper">
		<div class="catalog-overview-table-wrapper__left js-static-table-wrapper">

			<ProductTable :mobile-view-active="mobileViewActive"
			              :products="products"
			              :sort="tableSort"
			              :table-headers="productHeaders"
			              @sort-updated="updateSort($event)" />

			<button v-show="!isScrollStart && !mobileViewActive"
			        class="tablenavigation tablenavigation--prev"
			        @click="scrollToPrevColumn">

				<svg class="icon-triangle-left" height="11" width="8">
					<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#triangle-left"></use>
				</svg>
			</button>
		</div>

		<div v-show="!mobileViewActive"
		     v-scroll="onScroll"
		     ref="scrollElement"
		     class="catalog-overview-table-wrapper__right js-dynamic-table-wrapper">

			<DetailTable :mobile-view-active="mobileViewActive"
			             :products="products"
			             :sort="tableSort"
			             :table-headers="detailHeaders"
			             @sort-updated="updateSort($event)" />
		</div>

		<button v-show="!isScrollEnd && !mobileViewActive"
		        class="tablenavigation tablenavigation--next"
		        @click="scrollToNextColumn">

			<svg class="icon-triangle-right" height="11" width="8">
				<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#triangle-right"></use>
			</svg>
		</button>
	</div>
</template>
